import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Pencil, X, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui-kit/Dialog";
import { Button } from "@/components/ui-kit/Button";
import { Input, Label } from "@/components/ui-kit/Input";

function TemplateForm({ initial, onCancel, onSubmit }) {
  const [label, setLabel] = useState(initial?.label || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [hours, setHours] = useState(initial?.default_hours != null ? String(initial.default_hours) : "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!label.trim()) return;
    setSaving(true);
    await onSubmit({
      label: label.trim(),
      description: description.trim(),
      default_hours: hours === "" ? null : Number(hours),
    });
    setSaving(false);
  };

  return (
    <div className="space-y-3 rounded-md border border-border bg-secondary/40 p-3" data-testid="template-form">
      <div>
        <Label>Label</Label>
        <Input value={label} onChange={(e) => setLabel(e.target.value)} data-testid="template-label-input" placeholder="e.g. Mediation" />
      </div>
      <div>
        <Label>Description</Label>
        <Input value={description} onChange={(e) => setDescription(e.target.value)} data-testid="template-desc-input" placeholder="optional" />
      </div>
      <div>
        <Label>Default hours (optional)</Label>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            step="0.1"
            min="0"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            data-testid="template-hours-input"
            placeholder="none"
            className="max-w-[120px] nums"
          />
          {hours !== "" && (
            <button type="button" onClick={() => setHours("")} data-testid="template-clear-hours" className="text-xs text-muted-foreground hover:text-destructive">
              Remove default
            </button>
          )}
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button size="sm" onClick={submit} disabled={saving} data-testid="template-save">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save
        </Button>
      </div>
    </div>
  );
}

export function TemplateManager({ onChanged }) {
  const [templates, setTemplates] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");

  const load = () => api.get("/activity-templates").then((r) => setTemplates(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const wrap = (fn) => async (...args) => {
    setError("");
    try {
      await fn(...args);
      load();
      onChanged?.();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const create = wrap(async (body) => {
    await api.post("/activity-templates", body);
    setAdding(false);
  });
  const update = (id) =>
    wrap(async (body) => {
      await api.patch(`/activity-templates/${id}`, body);
      setEditingId(null);
    });
  const remove = wrap(async (id) => {
    await api.delete(`/activity-templates/${id}`);
  });

  return (
    <div className="space-y-3" data-testid="template-manager">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Reusable activities. A default time pre-fills Hours when picked.</p>
        {!adding && (
          <Button size="sm" onClick={() => setAdding(true)} data-testid="template-add">
            <Plus className="h-4 w-4" /> Add template
          </Button>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {adding && <TemplateForm onCancel={() => setAdding(false)} onSubmit={create} />}

      {templates === null ? (
        <div className="grid place-items-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
      ) : (
        <div className="space-y-2">
          {templates.map((t) =>
            editingId === t.id ? (
              <TemplateForm key={t.id} initial={t} onCancel={() => setEditingId(null)} onSubmit={update(t.id)} />
            ) : (
              <div
                key={t.id}
                data-testid={`template-row-${t.id}`}
                className="flex items-center gap-3 rounded-md border border-border bg-surface px-3 py-2"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{t.label}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {t.description || "—"}
                    {t.default_hours != null && <span className="ml-2 text-primary">· {t.default_hours}h default</span>}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setEditingId(t.id)}
                  data-testid={`template-edit-${t.id}`}
                  className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                  title="Edit"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => remove(t.id)}
                  data-testid={`template-delete-${t.id}`}
                  className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-destructive"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

export function TemplateManagerDialog({ open, onOpenChange, onChanged }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="template-manager-dialog">
        <DialogTitle>Manage activity templates</DialogTitle>
        <DialogDescription>Add, edit, or remove the activities Linda picks from.</DialogDescription>
        <div className="mt-4 max-h-[60vh] overflow-y-auto pr-1">
          <TemplateManager onChanged={onChanged} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
